// htop-style per-core CPU strip.
//
// Each row is a 3-column grid: [core index] [stacked bar] [pct].
// Segment colors mirror htop's defaults (green=user, blue=nice, red=sys,
// purple=iowait, yellow=softirq, cyan=irq, gray=steal). Stack order is
// front-to-back so the most "interesting" categories sit on top.
//
// Layout is pure Tailwind — no canvas. The auto-grid switches between
// 1 / 2 / 4 columns of cores depending on viewport, so 32 cores show as
// 8x4 on a wide screen and 32x1 on mobile.

import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from "@veil/ui/components/empty";
import { cn } from "@veil/ui/lib/utils";
import { CpuIcon } from "lucide-react";

export interface CpuSlice {
	usrPct: number;
	nicePct: number;
	sysPct: number;
	iowaitPct: number;
	irqPct: number;
	softirqPct: number;
	stealPct: number;
	idlePct: number;
	totalPct: number;
}

export interface CpuMetrics {
	aggregate: CpuSlice;
	perCore?: Array<CpuSlice & { cpu: number }>;
	source: "proc" | "os";
}

interface Props {
	cpu?: CpuMetrics | null;
	className?: string;
}

interface SegmentDef {
	key: keyof Omit<CpuSlice, "idlePct" | "totalPct">;
	className: string;
	label: string;
}

// Stack order matches htop: usr first (drawn on top in z-order), steal
// last. Render order in DOM = visual stacking front to back.
const SEGMENTS: SegmentDef[] = [
	{ key: "usrPct", className: "bg-emerald-500", label: "user" },
	{ key: "nicePct", className: "bg-sky-500", label: "nice" },
	{ key: "sysPct", className: "bg-rose-500", label: "system" },
	{ key: "iowaitPct", className: "bg-violet-500", label: "iowait" },
	{ key: "softirqPct", className: "bg-amber-400", label: "softirq" },
	{ key: "irqPct", className: "bg-cyan-500", label: "irq" },
	{ key: "stealPct", className: "bg-zinc-500", label: "steal" },
];

function CoreBar({
	slice,
	indexLabel,
}: {
	slice: CpuSlice;
	indexLabel: string;
}) {
	let offset = 0;
	return (
		<div className="grid grid-cols-[2.5ch_1fr_4ch] items-center gap-2">
			<span className="text-[10px] font-mono uppercase text-muted-foreground tabular-nums">
				{indexLabel}
			</span>
			<div className="relative h-3 overflow-hidden rounded-sm bg-muted/40">
				{SEGMENTS.map((seg) => {
					const raw = slice[seg.key];
					const pct = Math.max(0, Math.min(100, raw));
					if (pct <= 0) return null;
					const left = offset;
					offset += pct;
					return (
						<div
							key={seg.key}
							className={cn("absolute inset-y-0", seg.className)}
							style={{ left: `${left}%`, width: `${pct}%` }}
							title={`${seg.label} ${pct.toFixed(1)}%`}
						/>
					);
				})}
			</div>
			<span className="text-right font-mono text-xs tabular-nums text-foreground">
				{slice.totalPct.toFixed(0)}%
			</span>
		</div>
	);
}

export function CpuStrip({ cpu, className }: Props) {
	if (!cpu) {
		return (
			<Empty className={cn("border", className)}>
				<EmptyHeader>
					<EmptyMedia variant="icon">
						<CpuIcon />
					</EmptyMedia>
					<EmptyTitle>Per-core metrics unavailable</EmptyTitle>
					<EmptyDescription>
						Upgrade worker to receive CPU strip.
					</EmptyDescription>
				</EmptyHeader>
			</Empty>
		);
	}

	const cores = cpu.perCore ?? [];
	const showAggregateOnly = cores.length === 0;

	return (
		<div className={cn("flex flex-col gap-3", className)}>
			<div className="grid grid-cols-1 gap-x-6 gap-y-1.5 md:grid-cols-2 xl:grid-cols-4">
				{showAggregateOnly ? (
					<CoreBar slice={cpu.aggregate} indexLabel="all" />
				) : (
					cores.map((c) => (
						<CoreBar
							key={c.cpu}
							slice={c}
							indexLabel={c.cpu.toString().padStart(2, "0")}
						/>
					))
				)}
			</div>
			<Legend />
		</div>
	);
}

function Legend() {
	return (
		<div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] uppercase text-muted-foreground">
			{SEGMENTS.map((seg) => (
				<span key={seg.key} className="inline-flex items-center gap-1.5">
					<span
						className={cn("inline-block size-2 rounded-sm", seg.className)}
						aria-hidden
					/>
					{seg.label}
				</span>
			))}
		</div>
	);
}
